import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { RecipeService } from '@services/recipe/recipe.service';
import { RefDataService } from '@services/ref-data.service';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { ApplianceGroup, DataRef, DataRefsByKey, Status, Unit, Utensil } from '@shared/models/dataref.model';
import { RecipeForm } from '@shared/models/recipe/recipe-form.model';

@Component({
  selector: 'app-recipes-form-edit',
  templateUrl: './recipes-form-edit.component.html',
  styleUrls: ['./recipes-form-edit.component.scss'],
  standalone: false,
})
export class RecipesFormEditComponent implements OnChanges, OnInit {
  @Input() formRecipe: FormGroup<RecipeForm>;
  @Input() recipeOriginal?: RecipeLite;
  @Input() yieldUnits: DataRefsByKey<Unit> | null = {};

  @Output() updateRecipe = new EventEmitter<Partial<RecipeLite>>();

  typeAbaque = false;
  variants$?: Observable<RecipeLite[]>;
  applianceGroupsRefData$?: Observable<DataRefsByKey<ApplianceGroup>>;
  utensilsRefData$?: Observable<DataRefsByKey<Utensil>>;
  statusesRefData$?: Observable<DataRefsByKey<Status>>;
  variantDirty = false;
  isSeoURLDisabled$ = new BehaviorSubject<boolean>(false);

  triggerReloadVariants$ = new BehaviorSubject<void>(undefined);

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private recipeService: RecipeService,
    private refDataService: RefDataService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.typeAbaque = params.type === 'abacus';
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const recipe: RecipeLite = changes.recipeOriginal?.currentValue;

    if (recipe?.identifier) {
      this.variants$ = this.triggerReloadVariants$.pipe(
        switchMap(() => this.recipeService.getVariantsInfo(recipe.identifier.sourceSystem, recipe.groupingId)),
        tap((variants) => {
          const hasStatus = variants.some((variant) => variant.status === 'PUBLISHED' || variant.status === 'VALIDATED_MARKET');
          this.isSeoURLDisabled$.next(hasStatus);
        }),
        shareReplay(1),
      );
    }

    if (
      recipe?.domain &&
      recipe?.market &&
      recipe.lang &&
      (!this.applianceGroupsRefData$ || !this.statusesRefData$ || !this.utensilsRefData$)
    ) {
      this.applianceGroupsRefData$ = this.refDataService.getByKey('applianceGroups', true, recipe.domain, recipe.market, recipe.lang).pipe(
        map((allApplianceGroups) => this.convertRefData<ApplianceGroup>(allApplianceGroups, recipe)),
        shareReplay(1),
      );
      this.statusesRefData$ = this.refDataService.getByKey('statuses', true, recipe.domain, recipe.market, recipe.lang).pipe(
        map((allStatuses) => this.convertRefData(allStatuses, recipe)),
        shareReplay(1),
      );
      this.utensilsRefData$ = this.refDataService.getByKey('utensils', true, recipe.domain, recipe.market, recipe.lang).pipe(
        map((allUtensils) => this.convertRefData(allUtensils, recipe)),
        shareReplay(1),
      );
    }
  }

  private convertRefData<T extends DataRef>(refData: DataRefsByKey<T>, recipe: RecipeLite): DataRefsByKey<T> {
    return this.refDataService.toKeyValueObject(
      this.refDataService.translateReference(this.refDataService.toArray(refData), recipe.market, recipe.lang),
    );
  }
}
